import z from "zod";

export const rawPhoneNumberSchema = z.string().brand<"rawPhoneNumber">();
export type RawPhoneNumber = z.infer<typeof rawPhoneNumberSchema>;
export const localizedPhoneNumberSchema = z
  .string()
  .brand<"localizedPhoneNumber">();
export type LocalizedPhoneNumber = z.infer<typeof localizedPhoneNumberSchema>;

// https://en.wikipedia.org/wiki/List_of_telephone_country_codes
const KNOWN_TELEPHONE_COUNTRY_CODES = {
  CH: 41,
  LV: 371,
  SE: 46,
} as const;

const LOCAL_PREFIX = {
  CH: "0",
  LV: "",
  SE: "0",
};

export const standardizePhoneNumber = ({
  countryCode,
  phoneNumber,
}: {
  countryCode: keyof typeof KNOWN_TELEPHONE_COUNTRY_CODES;
  phoneNumber: LocalizedPhoneNumber;
}): RawPhoneNumber => {
  const telephoneCountryCode = KNOWN_TELEPHONE_COUNTRY_CODES[countryCode];
  const spacelessPhoneNumber = phoneNumber.replaceAll(" ", "");
  const standardizedPhoneNumber =
    spacelessPhoneNumber.startsWith(LOCAL_PREFIX[countryCode]) &&
    !spacelessPhoneNumber.startsWith("00")
      ? `00${telephoneCountryCode}${spacelessPhoneNumber.substring(LOCAL_PREFIX[countryCode].length)}`
      : spacelessPhoneNumber;
  return standardizedPhoneNumber as RawPhoneNumber;
};

export const localizePhoneNumber = ({
  countryCode,
  phoneNumber,
}: {
  countryCode: keyof typeof KNOWN_TELEPHONE_COUNTRY_CODES;
  phoneNumber: RawPhoneNumber;
}): LocalizedPhoneNumber => {
  const telephoneCountryCode = KNOWN_TELEPHONE_COUNTRY_CODES[countryCode];
  const expectedPrefix = `00${telephoneCountryCode}`;
  return (
    phoneNumber.startsWith(expectedPrefix)
      ? `${LOCAL_PREFIX[countryCode]}${phoneNumber.substring(expectedPrefix.length)}`
      : phoneNumber
  ) as LocalizedPhoneNumber;
};

// Format from https://learn.microsoft.com/en-us/microsoftteams/platform/concepts/build-and-test/deep-link-workflow?tabs=teamsjs-v2%2Cweb#configure-deep-link-manually-to-start-audio-video-call-with-users
export const MICROSOFT_TEAMS_PHONE_CALL_PREFIX =
  "msteams://teams.microsoft.com/l/call/0/0?users=4:";
